import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutline';
import {
  deleteResident,
  listResidents,
  updateResident,
  type PublicResident,
} from '../../lib/residents-api';
import {
  matchesResident,
  residentCounts,
  residentUnit,
  sortResidents,
  type ResidentFilter,
} from '../../lib/resident-directory';
import { FilterChips } from '../../components/FilterChips';
import { initialsOf } from '../../components/EditProfileDialog';
import { extractApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/format';
import { useGetIdentity } from '@refinedev/core';
import { useSearchParams } from 'react-router-dom';
import { InviteDialog, TeamMembersPanel, useTeamScope, type TeamIdentity } from './team';
import { useT } from '../../i18n';

const FILTERS: Array<{ key: ResidentFilter; label: string }> = [
  { key: 'all', label: 'res.filterAll' },
  { key: 'ACTIVE', label: 'res.chipActive' },
  { key: 'PENDING', label: 'res.chipPending' },
  { key: 'SUSPENDED', label: 'res.chipSuspended' },
];

type PageTab = 'residents' | 'team';

/**
 * Everyone in the cluster in one place: residents, and the cluster's own team
 * (admins, staff, security). Inviting either kind starts from the button here.
 */
export function ResidentsListPage() {
  const t = useT();
  const { data: identity } = useGetIdentity<TeamIdentity & { permissions?: string[] }>();
  const perms = identity?.permissions ?? [];
  const canResidents = perms.includes('can_manage_residents');
  const canTeam = perms.includes('can_manage_staff');
  const [params, setParams] = useSearchParams();
  const tab: PageTab = !canResidents || (canTeam && params.get('tab') === 'team') ? 'team' : 'residents';
  const changeTab = (next: PageTab) => setParams(next === 'team' ? { tab: 'team' } : {}, { replace: true });
  const scope = useTeamScope(identity, canTeam);
  const [inviting, setInviting] = useState(false);
  // Bumped after an invite so the team list reloads.
  const [teamVersion, setTeamVersion] = useState(0);
  const [residents, setResidents] = useState<PublicResident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ResidentFilter>('all');
  const [query, setQuery] = useState('');
  const [block, setBlock] = useState('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; resident: PublicResident } | null>(null);
  const [deleting, setDeleting] = useState<PublicResident | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteResident(deleting.id);
      setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
      setInfo(t('res.deletedInfo', { name: deleting.full_name }));
      setDeleting(null);
    } catch (e) {
      const { code, message } = extractApiError(e);
      if (code === 'ERR_NOT_FOUND') {
        setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
        setInfo(t('res.alreadyRemoved'));
        setDeleting(null);
      } else {
        setDeleteError(message);
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setResidents(await listResidents());
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canResidents) refresh();
  }, [canResidents]);

  async function setStatus(r: PublicResident, status: 'ACTIVE' | 'SUSPENDED') {
    setBusyId(r.id);
    setError(null);
    try {
      const updated = await updateResident(r.id, { status });
      setResidents((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  const counts = useMemo(() => residentCounts(residents), [residents]);
  const blocks = useMemo(
    () => [...new Set(residents.map((r) => r.unit_block).filter((b): b is string => b !== null))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [residents],
  );
  const shown = useMemo(
    () =>
      sortResidents(
        residents.filter(
          (r) =>
            (filter === 'all' || r.status === filter) &&
            (block === 'all' || r.unit_block === block) &&
            matchesResident(r, query),
        ),
      ),
    [residents, filter, block, query],
  );

  // Draw one page of rows at a time; search and filters still cover everyone.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  useEffect(() => setPage(0), [filter, block, query, rowsPerPage]);
  const pageRows = shown.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t(canResidents && canTeam ? 'res.titleBoth' : tab === 'team' ? 'team.title' : 'res.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(tab === 'team' ? 'res.subtitleTeam' : 'res.subtitleShort')}
          </Typography>
        </div>
        {canTeam ? (
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInviting(true)} sx={{ flexShrink: 0 }}>
            {t('res.invite')}
          </Button>
        ) : null}
      </Stack>

      {canResidents && canTeam ? (
        <Tabs value={tab} onChange={(_, v: PageTab) => changeTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="residents" label={t('res.tabResidents')} />
          <Tab value="team" label={t('res.tabTeam')} />
        </Tabs>
      ) : null}

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {info ? (
        <Alert severity="info" onClose={() => setInfo(null)}>
          {info}
        </Alert>
      ) : null}

      {tab === 'team' ? (
        <TeamMembersPanel scope={scope} identity={identity} version={teamVersion} />
      ) : (
      <>
        <FilterChips
          chips={FILTERS.map((f) => ({ key: f.key, label: t(f.label), count: counts[f.key] }))}
          value={filter}
          onChange={setFilter}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('res.search')}
            sx={{ width: { xs: '100%', sm: 320 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select size="small" value={block} onChange={(e) => setBlock(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="all">{t('units.blockAll')}</MenuItem>
            {blocks.map((b) => (
              <MenuItem key={b} value={b}>{b}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('res.showing', { shown: shown.length, total: residents.length })}
          </Typography>
        </Stack>

        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : shown.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {residents.length === 0 ? t('res.emptyAll') : t('res.emptyFiltered')}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('res.colResident')}</TableCell>
                      <TableCell sx={{ width: 160 }}>{t('res.colUnit')}</TableCell>
                      <TableCell sx={{ width: 160 }}>{t('common.status')}</TableCell>
                      <TableCell sx={{ width: 150, display: { xs: 'none', md: 'table-cell' } }}>{t('res.colJoined')}</TableCell>
                      <TableCell sx={{ width: 56 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((r) => {
                      const unit = residentUnit(r);
                      return (
                        <TableRow key={r.id} hover>
                          {/* Who: initials, name (star for the household's primary), email. */}
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: 'action.selected', color: 'text.primary' }}>
                                {initialsOf(r.full_name)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="body2" fontWeight={600} noWrap>{r.full_name}</Typography>
                                  {r.is_primary ? (
                                    <Tooltip title={t('res.mainUser')}>
                                      <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} aria-label={t('res.mainUser')} />
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" noWrap component="div">{r.email}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {unit ? (
                              <Typography variant="body2" fontWeight={600}>{unit}</Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">{t('res.notLinked')}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusDot status={r.status} />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" color="text.secondary">{formatDate(r.created_at.slice(0, 10))}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label={t('units.more')}
                              onClick={(e) => setMenu({ anchor: e.currentTarget, resident: r })}
                              disabled={busyId === r.id}
                            >
                              {busyId === r.id ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!loading && shown.length > 0 ? (
              <TablePagination
                component="div"
                count={shown.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={t('common.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('common.displayedRows', { from, to, count })}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            ) : null}
          </CardContent>
        </Card>

        <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
          {menu?.resident.status === 'SUSPENDED' ? (
            <MenuItem onClick={() => { const r = menu.resident; setMenu(null); void setStatus(r, 'ACTIVE'); }}>
              <ListItemIcon><PlayCircleIcon fontSize="small" /></ListItemIcon>
              {t('res.reactivate')}
            </MenuItem>
          ) : (
            <MenuItem onClick={() => { const r = menu!.resident; setMenu(null); void setStatus(r, 'SUSPENDED'); }}>
              <ListItemIcon><PauseCircleIcon fontSize="small" /></ListItemIcon>
              {t('res.suspend')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => { const r = menu!.resident; setMenu(null); setDeleteError(null); setDeleting(r); }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}><DeleteIcon fontSize="small" /></ListItemIcon>
            {t('common.delete')}
          </MenuItem>
        </Menu>
      </>
      )}

      {canTeam ? (
        <InviteDialog
          open={inviting}
          scope={scope}
          identity={identity}
          existingEmails={residents.map((r) => r.email)}
          onClose={() => setInviting(false)}
          onInvited={(message, role) => {
            setInviting(false);
            setInfo(message);
            // A new resident shows in the resident list, anyone else in the team list.
            if (role === 'resident') {
              if (canResidents) void refresh();
            } else {
              setTeamVersion((v) => v + 1);
            }
            changeTab(role === 'resident' ? 'residents' : 'team');
          }}
          onImported={() => {
            if (canResidents) void refresh();
            changeTab('residents');
          }}
        />
      ) : null}

      <Dialog
        open={!!deleting}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('res.deleteTitle', { name: deleting?.full_name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('res.deleteWarning')}
            </Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** A coloured dot and the status name: calmer than a filled chip on every row. */
function StatusDot({ status }: { status: PublicResident['status'] }) {
  const t = useT();
  const color = status === 'ACTIVE' ? 'success.main' : status === 'SUSPENDED' ? 'error.main' : 'warning.main';
  const label = status === 'ACTIVE' ? t('res.chipActive') : status === 'SUSPENDED' ? t('res.chipSuspended') : t('res.chipPending');
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}
